import React, { createContext, useContext, useState, useEffect } from "react";
import { api } from "../api";
import { isDevMode } from "../lib/isDevMode";

type User = {
  id: string;
  email: string;
  fullName: string;
  role: string;
};

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  loginWithMicrosoft: () => void;
  login?: (email: string, password: string) => Promise<User | undefined>;
  logout: () => void;
};

const Ctx = createContext<AuthContextValue>({} as AuthContextValue);

const STORAGE_KEYS = ["hmh-user", "hmh_user"] as const;

function clearStoredUser() {
  for (const key of STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

function readStoredUser(): { user: User; token: string } | null {
  for (const key of STORAGE_KEYS) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.id && parsed?.email && parsed?.role && parsed?.token) {
        return {
          user: {
            id: parsed.id,
            email: parsed.email,
            fullName: parsed.fullName ?? "",
            role: parsed.role,
          },
          token: parsed.token,
        };
      }
    } catch {
      localStorage.removeItem(key);
    }
  }
  return null;
}

function persistUser(user: User, token: string) {
  const payload = { ...user, token };
  localStorage.setItem("hmh-user", JSON.stringify(payload));
}

function userFromResponse(data: User): User {
  return {
    id: data.id,
    email: data.email,
    fullName: data.fullName ?? "",
    role: data.role,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const res = await api.get("/auth/me");
        if (cancelled) return;
        setUser(userFromResponse(res.data as User));
        return;
      } catch {
        if (isDevMode()) {
          clearStoredUser();
        }
      }

      if (!cancelled) {
        setUser(null);
      }
    };

    void init().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithMicrosoft = () => {
    window.location.href = "/api/auth/entra/login";
  };

  const logout = () => {
    setUser(null);
    clearStoredUser();
    window.location.href = "/api/auth/logout";
  };

  const value: AuthContextValue = {
    user,
    loading,
    loginWithMicrosoft,
    logout,
  };

  if (isDevMode()) {
    value.login = async (email: string, password: string) => {
      setLoading(true);
      try {
        const res = await api.post("/auth/login", { email, password });
        const u = userFromResponse(res.data.user as User);
        persistUser(u, res.data.token as string);
        setUser(u);
        return u;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        const message =
          axiosErr.response?.data?.error ||
          (isDevMode() ? "Invalid email or password" : "Login failed");
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    };
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAuth = () => useContext(Ctx);

export const getAuthToken = () => {
  if (!isDevMode()) return null;
  return readStoredUser()?.token ?? null;
};
